// Khối của trang Tổng quan — dùng chung cho Shop / Bán hàng, CTV, Đại lý, NPP. Màu nhấn lấy từ token theo cấp
// (--accent, --band) nên không khối nào tự chọn màu cấp. 30/10/2026 rút gọn: chỉ còn doanh thu / doanh số, số đơn
// (vòng trạng thái) và — cấp quản lý — đơn vị trực thuộc, theo kỳ Ngày · Tháng · Năm (01/11/2026) hoặc chọn lịch từ–đến.
//   · useOverviewRange — khoảng đang xem (kỳ: sang ngày khác về kỳ hiện tại; tự chọn giữ đúng ngày), nhớ theo trình duyệt
//   · rangeQuery / rangePeriod — tham số ?from=&to= cho API, chữ khoảng ở góc ô tiền
//   · OverviewHead    — "Tổng quan" + chọn khoảng (điện thoại: hàng riêng dưới tiêu đề)
//   · RevenueHero     — tiền của khoảng: số to + biểu đồ cột có trục tiền (một ngày: theo giờ)
//   · revenuePoints   — cột cho RevenueHero từ `daily` / `hourly` của API
//   · OrdersCard      — vòng trạng thái đơn; số giữa vòng = tổng đơn (không tính đơn hủy)
//   · OrderStatusCard — Bán hàng: vòng to + ô từng trạng thái, đứng đầu trang (31/10/2026)
//   · MoneyCard       — thẻ số có thanh chia phần + dòng từng phần (đơn vị trực thuộc)
//   · OverviewGrid    — máy tính: tiền | (đơn vị + đơn hàng); điện thoại: một cột

import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { CalendarDays, ChevronRight, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { money } from '@/lib/format'
import { useViewportMode } from '@/hooks/useViewportMode'
import { Skeleton } from '@/components/ui/skeleton'
import { PageHeader } from '@/components/ui/page-header'
import { Segmented } from '@/components/ui/segmented'
import { Section } from '@/components/ui/section'
import { LoadError } from '@/components/ui/data-notice'
import { toneClass, type Tone } from '@/components/ui/kpi-tile'
import { Donut, DonutRing } from '@/components/charts/Donut'
import { statusSegments, type StatusCounts } from '@/components/charts/statusSegments'
import { SeasonHello } from '@/components/season/Season'
import { useSeason } from '@/lib/season/store'
import { compactMoney, countFormat, fillRange, longDate, niceScale, shortDate } from '@/components/charts/chart-utils'
import { PERIOD_UNITS, PeriodStepper } from '@/components/ui/period-picker'
import {
  clampPeriod,
  currentPeriod,
  isDateKey,
  periodLabel,
  periodRange,
  periodUnitOf,
  rangeDays,
  rangeSummary,
  switchPeriodUnit,
  vnDateKey,
  vnHour,
  type PeriodUnit,
} from '@shoptrack/common/date-range'

/** Khoảng Từ – đến dài nhất (kỳ Năm thì cả năm — API nhận tới 366 ngày, common/date-range OVERVIEW_MAX_DAYS). */
export const MAX_DAYS = 90
const RANGE_KEY = 'st-overview-range'

/** Khoảng đang xem theo ngày VN, hai đầu đều tính. */
export interface OverviewRange {
  /** Kỳ Ngày / Tháng / Năm, hoặc tự chọn Từ – đến */
  key: PeriodUnit | 'custom'
  /** Mã kỳ ("2026-11-01" · "2026-11" · "2026") — khi key ≠ custom */
  period?: string
  from: string
  /** Ngày cuối gọi báo cáo — không quá hôm nay */
  to: string
  /** Ngày cuối của cả kỳ — biểu đồ vẽ đủ kỳ, ngày / tháng chưa tới để trống */
  last: string
}

const addDays = (key: string, n: number) => new Date(Date.parse(`${key}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10)

const ofPeriod = (period: string): OverviewRange => ({ key: periodUnitOf(period) ?? 'day', period, ...periodRange(period) })

/**
 * Khoảng đã nhớ → khoảng dùng. Kỳ: cùng ngày thì mở lại đúng kỳ đã xem, sang ngày khác thì về kỳ hiện tại (Hôm nay /
 * tháng này / năm nay). Tự chọn: giữ ngày (không quá hôm nay, ≤ 90 ngày). Mốc cũ: today → Ngày, 7d / 30d → Tháng.
 */
function resolveRange(saved: unknown): OverviewRange {
  const v = (saved ?? {}) as { key?: string; period?: string; on?: string; from?: string; to?: string }
  const today = vnDateKey(new Date())
  if (v.key === 'custom' && isDateKey(v.from) && isDateKey(v.to)) {
    let [from, to] = v.from <= v.to ? [v.from, v.to] : [v.to, v.from]
    if (to > today) to = today
    if (from > to) from = to
    if (rangeDays(from, to) > MAX_DAYS) from = addDays(to, 1 - MAX_DAYS)
    return { key: 'custom', from, to, last: to }
  }
  const unit: PeriodUnit = v.key === 'month' || v.key === '7d' || v.key === '30d' ? 'month' : v.key === 'year' ? 'year' : 'day'
  const keep = v.on === today && periodUnitOf(v.period) === unit
  return ofPeriod(keep ? clampPeriod(v.period, unit) : currentPeriod(unit))
}

/** Khoảng đang xem; mặc định Hôm nay. Nhớ theo trình duyệt — chỉ là tiện cho người xem. */
export function useOverviewRange(): [OverviewRange, (next: OverviewRange) => void] {
  const [range, setRange] = useState<OverviewRange>(() => {
    try {
      return resolveRange(JSON.parse(localStorage.getItem(RANGE_KEY) ?? 'null'))
    } catch {
      return resolveRange(null)
    }
  })
  const set = useCallback((next: OverviewRange) => {
    setRange(next)
    try {
      const { key, period, from, to } = next
      localStorage.setItem(RANGE_KEY, JSON.stringify(key === 'custom' ? { key, from, to } : { key, period, on: vnDateKey(new Date()) }))
    } catch {}
  }, [])
  return [range, set]
}

/** Tham số gọi API (/reports/revenue, /reports/agency-network, admin /overview/orders). */
export const rangeQuery = (r: OverviewRange) => `from=${r.from}&to=${r.to}`

const dayLabel = (key: string) =>
  new Date(`${key}T00:00:00Z`).toLocaleDateString('vi-VN', {
    weekday: 'long',
    day: 'numeric',
    month: 'numeric',
    ...(key.slice(0, 4) === vnDateKey(new Date()).slice(0, 4) ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  })

/** Chữ khoảng ở góc ô tiền: "thứ sáu, 30/10" · "Tháng 10/2026" · "Năm 2026" · "01/10 – 15/10/2026". */
export function rangePeriod(r: OverviewRange): string {
  if (r.key === 'month' || r.key === 'year') return periodLabel(r.period!)
  return r.from === r.to ? dayLabel(r.from) : rangeSummary(r.from, r.to)
}

const dm = (key: string) => `${Number(key.slice(8, 10))}/${Number(key.slice(5, 7))}`

const DATE_INPUT =
  'h-11 min-w-0 flex-1 rounded-lg border border-rule bg-raise px-2.5 text-base tabular-nums text-ink desktop:h-8 desktop:w-36 desktop:flex-none desktop:text-xs focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/20'

/**
 * Ngày · Tháng · Năm (hàng dưới: ‹ kỳ › lùi / tiến từng kỳ) + "Từ – đến": hai ô lịch (lịch có sẵn của máy). Đổi ô lịch
 * xong dừng tay 0,5 giây mới áp dụng — chỉnh liền hai ô chỉ gọi báo cáo một lần (báo cáo mạng lưới đọc từng shop,
 * nặng). Khoảng dài quá 90 ngày thì ô còn lại tự lùi / tiến theo.
 */
function RangePicker({ range, onChange }: { range: OverviewRange; onChange: (next: OverviewRange) => void }) {
  const today = vnDateKey(new Date())
  const custom = range.key === 'custom'
  const [draft, setDraft] = useState({ from: range.from, to: range.to })
  useEffect(() => setDraft({ from: range.from, to: range.to }), [range.from, range.to])
  useEffect(() => {
    if (!custom || (draft.from === range.from && draft.to === range.to)) return
    const t = setTimeout(() => onChange({ key: 'custom', ...draft, last: draft.to }), 500)
    return () => clearTimeout(t)
  }, [draft]) // eslint-disable-line react-hooks/exhaustive-deps
  // Đổi loại kỳ giữ mốc đang xem (Từ – đến: theo ngày cuối)
  const pick = (u: PeriodUnit) => {
    if (u !== range.key) onChange(ofPeriod(switchPeriodUnit(range.period ?? range.to, u)))
  }
  // Từ – đến bắt đầu từ khoảng đang xem (kỳ Năm dài hơn 90 ngày → 90 ngày cuối)
  const openCustom = () => {
    if (custom) return
    const from = rangeDays(range.from, range.to) > MAX_DAYS ? addDays(range.to, 1 - MAX_DAYS) : range.from
    onChange({ key: 'custom', from, to: range.to, last: range.to })
  }
  const setFrom = (v: string) => {
    if (!isDateKey(v)) return
    const from = v > today ? today : v
    let to = draft.to < from ? from : draft.to
    if (rangeDays(from, to) > MAX_DAYS) to = addDays(from, MAX_DAYS - 1)
    setDraft({ from, to: to > today ? today : to })
  }
  const setTo = (v: string) => {
    if (!isDateKey(v)) return
    const to = v > today ? today : v
    let from = draft.from > to ? to : draft.from
    if (rangeDays(from, to) > MAX_DAYS) from = addDays(to, 1 - MAX_DAYS)
    setDraft({ from, to })
  }
  return (
    // Điện thoại: giãn hết hàng; iPad (cũng là chế độ điện thoại) không giãn quá 28rem
    <div className="flex min-w-0 flex-col gap-2 mobile:max-w-md desktop:items-end">
      <div className="flex min-w-0 items-center gap-2">
        <Segmented<PeriodUnit | 'custom'>
          label="Xem theo"
          value={range.key}
          onChange={(k) => k !== 'custom' && pick(k)}
          options={PERIOD_UNITS}
          className="mobile:flex-1 mobile:[&>button]:flex-1"
        />
        <button
          type="button"
          aria-pressed={custom}
          onClick={openCustom}
          className={cn(
            'motion-state flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg bg-paperSoft px-2.5 text-xs font-medium',
            // Điện thoại: cùng dáng viên với nhóm nút bên cạnh (glass.css [data-slot='segmented'])
            'mobile:h-[34px] mobile:rounded-full mobile:border mobile:border-[rgb(var(--line))] mobile:px-3 mobile:text-[13px] mobile:font-semibold',
            custom ? 'bg-raise text-ink shadow-pop ring-1 ring-accent/40' : 'text-inkSoft hover:text-ink',
          )}
        >
          <CalendarDays className="size-3.5 shrink-0" aria-hidden />
          {custom ? (range.from === range.to ? dm(range.from) : `${dm(range.from)}–${dm(range.to)}`) : 'Từ – đến'}
        </button>
      </div>
      {custom ? (
        <div className="flex w-full items-center gap-1.5 desktop:w-auto">
          <input type="date" aria-label="Từ ngày" value={draft.from} max={today} onChange={(e) => setFrom(e.target.value)} className={DATE_INPUT} />
          <span className="text-muted" aria-hidden>
            –
          </span>
          <input type="date" aria-label="Đến ngày" value={draft.to} min={draft.from} max={today} onChange={(e) => setTo(e.target.value)} className={DATE_INPUT} />
        </div>
      ) : (
        <PeriodStepper value={range.period!} onChange={(k) => onChange(ofPeriod(k))} className="w-full desktop:w-auto" />
      )}
    </div>
  )
}

export function OverviewHead({ range, onRange }: { range: OverviewRange; onRange: (next: OverviewRange) => void }) {
  const phone = useViewportMode() === 'mobile'
  if (phone) {
    return (
      <div className="space-y-3">
        <PageHeader title="Tổng quan" />
        <RangePicker range={range} onChange={onRange} />
      </div>
    )
  }
  return <PageHeader title="Tổng quan" actions={<RangePicker range={range} onChange={onRange} />} />
}

/** Một cột của biểu đồ tiền: một ngày, một giờ của một ngày, hoặc một tháng của kỳ Năm. */
export interface ChartPoint {
  key: string
  value: number
  /** Nhãn trục ngang: "29/10" · "9h" · "T10" */
  tick: string
  /** Chữ khi chạm / rê: "T5, 29/10" · "9h–10h" · "Tháng 10/2026" */
  tip: string
  /** Hôm nay / giờ / tháng đang chạy — cột đậm */
  now?: boolean
  /** Giờ / ngày / tháng chưa tới */
  later?: boolean
}

/**
 * Cột của ô tiền: một ngày → 24 giờ theo giờ VN (`hourly` của API; hôm nay thì giờ chưa tới mờ đi); kỳ Năm → 12 tháng
 * (cộng `daily` theo tháng); còn lại mỗi ngày một cột từ `from` tới ngày cuối của kỳ (`daily`, ngày trống = 0; ngày
 * chưa tới của tháng này mờ đi). Bản API cũ không có `hourly` thì chỉ có số, không có cột.
 */
export function revenuePoints<D extends { date: string }, H extends { hour: number }>(
  range: OverviewRange,
  daily: D[] | undefined,
  hourly: H[] | undefined,
  pickDay: (d: D) => number,
  pickHour: (h: H) => number,
): ChartPoint[] {
  const today = vnDateKey(new Date())
  // Một ngày (kỳ Ngày / Từ – đến một ngày) — tháng này xem ngày mùng 1 vẫn là cột theo ngày
  if (range.from === range.last) {
    if (!hourly) return []
    const isToday = range.from === today
    const nowHour = vnHour(new Date())
    const byHour = new Map(hourly.map((h) => [h.hour, pickHour(h)]))
    return Array.from({ length: 24 }, (_, h) => ({
      key: `h${h}`,
      value: byHour.get(h) ?? 0,
      tick: `${h}h`,
      tip: `${h}h–${h + 1}h`,
      now: isToday && h === nowHour,
      later: isToday && h > nowHour,
    }))
  }
  if (range.key === 'year') {
    const year = range.from.slice(0, 4)
    const thisMonth = today.slice(0, 7)
    const byMonth = new Map<string, number>()
    for (const d of daily ?? []) byMonth.set(d.date.slice(0, 7), (byMonth.get(d.date.slice(0, 7)) ?? 0) + pickDay(d))
    return Array.from({ length: 12 }, (_, i) => {
      const key = `${year}-${String(i + 1).padStart(2, '0')}`
      return { key, value: byMonth.get(key) ?? 0, tick: `T${i + 1}`, tip: `Tháng ${i + 1}/${year}`, now: key === thisMonth, later: key > thisMonth }
    })
  }
  return fillRange(daily, range.from, range.last, pickDay).map((d) => ({
    key: d.date,
    value: d.value,
    tick: d.date === today ? 'Hôm nay' : shortDate(d.date),
    tip: d.date === today ? 'Hôm nay' : longDate(d.date),
    now: d.date === today,
    later: d.date > today,
  }))
}

/**
 * Vị trí nhãn trục ngang: 24 giờ → 0h 6h 12h 18h 23h; 12 tháng → máy tính mọi tháng, điện thoại T1 T4 T7 T10 T12;
 * máy tính ≤ 7 ngày → mọi ngày; còn lại 5 mốc (điện thoại 3 —
 * 7 nhãn "29/10" không vừa bề ngang điện thoại, nhãn cuối "Hôm nay" dính vào nhãn trước).
 */
function tickIndexes(points: ChartPoint[], phone: boolean): number[] {
  const n = points.length
  if (n === 24 && points[0]?.key === 'h0') return phone ? [0, 6, 12, 18, 23] : [0, 3, 6, 9, 12, 15, 18, 21, 23]
  if (n === 12 && points[0]?.key.length === 7) return phone ? [0, 3, 6, 9, 11] : points.map((_, i) => i)
  if (n <= (phone ? 3 : 7)) return points.map((_, i) => i)
  const parts = phone ? 2 : 4
  return [...new Set(Array.from({ length: parts + 1 }, (_, k) => Math.round(((n - 1) * k) / parts)))]
}

/**
 * Trục tiền của ô tiền: chỉ số dương → 0…đỉnh như cũ (niceScale); có số âm (ngày lỗ — Lợi nhuận NPP) → kéo xuống dưới 0
 * cùng bước chia; chưa có tiền → chỉ vạch 0 (niceScale của 0 ra các mốc 1 ₫, 2 ₫… vô nghĩa).
 */
export function axisScale(values: number[], count: number): { lo: number; hi: number; ticks: number[] } {
  const peak = Math.max(0, ...values)
  const trough = Math.min(0, ...values)
  if (peak === 0 && trough === 0) return { lo: 0, hi: 1, ticks: [0] }
  if (trough === 0) {
    const up = niceScale(peak, count)
    return { lo: 0, hi: up.max, ticks: up.ticks }
  }
  const step = niceScale(Math.max(peak, -trough), count).ticks[1]!
  const hi = Math.ceil(peak / step) * step
  const lo = -Math.ceil(-trough / step) * step
  const ticks: number[] = []
  for (let v = lo; v <= hi + step / 2; v += step) ticks.push(Math.round(v))
  return { lo, hi, ticks }
}

/** Doanh thu / doanh số của khoảng: số to + biểu đồ cột có trục tiền. Rê / chạm một cột xem cột đó. Khoác cảnh theo dịp. */
export function RevenueHero({
  label,
  total,
  points,
  period,
  hours,
  loading,
  error,
  onRetry,
  barMax,
}: {
  /** "Doanh thu" (Shop / Bán hàng) / "Doanh số" (cấp quản lý) */
  label: string
  total: number | undefined
  points: ChartPoint[]
  /** Chữ khoảng ở góc ô (rangePeriod) */
  period: string
  /** Khoảng một ngày — cột theo giờ */
  hours: boolean
  loading?: boolean
  error?: boolean
  onRetry?: () => void
  /** Cột rộng tối đa (px), đứng giữa ô của nó — ô tiền hết bề ngang (Bán hàng) mà ít ngày thì cột không to bè */
  barMax?: number
}) {
  const phone = useViewportMode() === 'mobile'
  // Điện thoại có dịp: cảnh nằm ở góc phải trên — chữ khoảng xuống dưới nhãn để không bị cảnh đè
  const { season } = useSeason()
  const cornerScene = phone && !!season
  const [active, setActive] = useState<number | null>(null)
  const n = points.length
  const scale = axisScale(points.map((p) => p.value), phone ? 2 : 4)
  const pct = (v: number) => ((v - scale.lo) / (scale.hi - scale.lo)) * 100
  const sel = active != null ? points[active] : undefined
  const ticks = tickIndexes(points, phone)

  return (
    <SeasonHello slot="hero" className="flex min-w-0 flex-1 flex-col gap-4 rounded-2xl bg-band p-5 desktop:px-6">
      <div className={cn('flex gap-3', cornerScene ? 'flex-col items-start gap-0' : 'items-center justify-between')}>
        <h2 data-hero="label" className="text-accentSoft">{label}</h2>
        <span data-hero="soft" className="text-xs text-inkSoft">{period}</span>
      </div>
      {loading ? (
        <Skeleton className="h-12 w-56 max-w-full bg-raise/70" />
      ) : (
        <span
          data-hero="ink"
          title={total != null ? money(total) : undefined}
          className={cn(
            'block min-w-0 truncate text-[52px] font-bold leading-none tracking-[-0.03em] text-ink mobile:text-[length:min(40px,9.5vw)]',
            !error && total != null && total < 0 && 'text-danger',
          )}
        >
          {error || total == null ? '—' : money(total)}
        </span>
      )}
      {error && (
        <span data-hero="soft" className="flex items-center gap-3 text-sm text-inkSoft">
          Không tải được số liệu
          {onRetry && (
            <button type="button" onClick={onRetry} data-hero="ink" className="motion-state -my-2.5 min-h-11 font-semibold text-ink underline desktop:my-0 desktop:min-h-0">
              Thử lại
            </button>
          )}
        </span>
      )}
      {loading && <Skeleton className="h-36 w-full bg-raise/50 mobile:h-[132px]" />}
      {!loading && !error && n > 0 && (
        // Máy tính: ô tiền cao bằng cột phải — biểu đồ giãn kín phần còn lại (tối thiểu 176px)
        <div className="mt-auto grid min-h-0 flex-1 grid-cols-[auto_minmax(0,1fr)] grid-rows-[minmax(0,1fr)_auto] gap-x-2 gap-y-2">
          {/* Trục tiền */}
          <div data-hero="soft" className="relative w-9 text-right text-[11px] leading-none tabular-nums text-inkSoft" aria-hidden>
            {scale.ticks.map((t) => (
              <span key={t} className="absolute right-0 translate-y-1/2" style={{ bottom: `${pct(t)}%` }}>
                {t === 0 ? '0' : compactMoney(t)}
              </span>
            ))}
          </div>
          <div
            className="relative h-[132px] desktop:h-auto desktop:min-h-44"
            role="img"
            aria-label={`${label} ${hours ? 'từng giờ' : points[0]?.key.length === 7 ? 'từng tháng' : `từng ngày trong ${n} ngày`} (${period}): tổng ${money(total ?? 0)}`}
            onPointerLeave={() => setActive(null)}
          >
            {/* Vạch ngang theo mốc trục tiền */}
            {scale.ticks.map((t) => (
              <span
                key={t}
                data-hero="soft"
                className={cn('absolute inset-x-0 border-t text-inkSoft', t === 0 ? 'border-solid opacity-40' : 'border-dashed opacity-20')}
                style={{ bottom: `${pct(t)}%`, borderColor: 'currentColor' }}
                aria-hidden
              />
            ))}
            <div className={cn('absolute inset-0 flex', n > 60 ? 'gap-px' : n > 31 ? 'gap-0.5' : 'gap-1 mobile:gap-[3px]')}>
              {points.map((p, i) => {
                const loss = p.value < 0
                const size = p.value === 0 ? 0 : Math.max((Math.abs(p.value) / (scale.hi - scale.lo)) * 100, 2)
                return (
                  <div key={p.key} className="relative h-full min-w-0 flex-1" onPointerEnter={() => setActive(i)} onPointerDown={() => setActive(i)}>
                    <span
                      data-hero={loss ? 'bar-loss' : p.now ? 'bar-today' : 'bar'}
                      data-active={i === active || undefined}
                      className={cn(
                        'absolute left-1/2 block w-full -translate-x-1/2 transition-colors duration-150',
                        loss ? 'rounded-b-[4px] rounded-t-[1px] mobile:rounded-b-[3px]' : 'rounded-t-[4px] rounded-b-[1px] mobile:rounded-t-[3px]',
                        loss ? (p.now || i === active ? 'bg-danger/80' : 'bg-danger/45') : p.now ? 'bg-accent' : i === active ? 'bg-accent/70' : 'bg-accent/40',
                        p.later && 'opacity-30',
                      )}
                      style={{ bottom: `${loss ? pct(p.value) : pct(0)}%`, height: `${size}%`, maxWidth: barMax }}
                    />
                  </div>
                )
              })}
            </div>
          </div>
          <span aria-hidden />
          {/* Trục ngang — chạm một cột thì thay bằng ngày / giờ và số tiền của cột đó */}
          <div data-hero="soft" className="relative h-3 text-[11px] leading-none text-inkSoft" aria-hidden>
            {sel ? (
              <span data-hero="ink" className="absolute inset-x-0 text-center font-semibold text-ink">
                {sel.tip} · {money(sel.value)}
              </span>
            ) : (
              ticks.map((i) => {
                const p = points[i]!
                const pos = n > 1 ? ((i + 0.5) / n) * 100 : 50
                return (
                  <span
                    key={p.key}
                    data-hero={p.now && !hours ? 'label' : undefined}
                    className={cn('absolute whitespace-nowrap', i === 0 ? 'left-0' : i === n - 1 ? 'right-0' : '-translate-x-1/2', p.now && !hours && 'font-semibold text-accentSoft')}
                    style={i === 0 || i === n - 1 ? undefined : { left: `${pos}%` }}
                  >
                    {p.tick}
                  </span>
                )
              })
            )}
          </div>
        </div>
      )}
    </SeasonHello>
  )
}

/** Vòng trạng thái đơn của khoảng — mọi số đơn của Tổng quan nằm ở đây. */
export function OrdersCard({
  counts,
  loading,
  error,
  onRetry,
}: {
  counts: StatusCounts | null | undefined
  loading?: boolean
  error?: boolean
  onRetry?: () => void
}) {
  const phone = useViewportMode() === 'mobile'
  return (
    <Section title="Đơn hàng" className="flex min-w-0 flex-1 flex-col">
      {error ? (
        <LoadError onRetry={onRetry} />
      ) : (
        <div className="flex flex-1 items-center p-4 desktop:px-5">
          {loading ? (
            <Skeleton className={cn('w-full rounded-xl', phone ? 'h-28' : 'h-36')} />
          ) : (
            // Không cột phần trăm: % đã giao / % hoàn chính là tỷ lệ giao / tỷ lệ boom — Tổng quan chỉ đếm đơn
            <Donut compact={phone} percent={false} className="w-full" emptyLabel="Chưa có đơn" segments={statusSegments(counts)} />
          )}
        </div>
      )}
    </Section>
  )
}

/**
 * Bán hàng (31/10/2026): số đơn của khoảng đứng đầu Tổng quan — vòng to, giữa vòng là tổng đơn (không tính đơn hủy),
 * bên cạnh (điện thoại: bên dưới) mỗi trạng thái một ô nền nhạt cùng màu cung. Rê / chạm ô hay cung thì cả hai sáng và
 * giữa vòng đổi sang số của trạng thái đó. "Khác" (chờ duyệt, hẹn gửi…) chỉ có ô khi có đơn. Không phần trăm.
 */
export function OrderStatusCard({
  counts,
  period,
  loading,
  error,
  onRetry,
}: {
  counts: StatusCounts | null | undefined
  /** Chữ khoảng ở góc thẻ (rangePeriod) */
  period: string
  loading?: boolean
  error?: boolean
  onRetry?: () => void
}) {
  const phone = useViewportMode() === 'mobile'
  const [active, setActive] = useState<string | null>(null)
  const segments = statusSegments(counts)
  const tiles = segments.filter((x) => x.key !== 'other' || x.value > 0)
  // 4 ô (5 khi có "Khác"): điện thoại 2 cột, iPad một hàng; máy tính 2×2 cạnh vòng, màn rộng (≥ 1280px) một hàng.
  // Số ô lẻ khi 2 cột: ô cuối nằm hết hàng.
  const five = tiles.length === 5
  const gridCols = phone ? cn('grid-cols-2', five ? 'sm:grid-cols-5' : 'sm:grid-cols-4') : cn('flex-1 grid-cols-2', five ? 'xl:grid-cols-5' : 'xl:grid-cols-4')
  const lastSpan = five ? (phone ? 'col-span-2 sm:col-span-1' : 'col-span-2 xl:col-span-1') : undefined
  const total = segments.reduce((sum, x) => sum + x.value, 0)
  const current = segments.find((x) => x.key === active)
  // Chạm lại ô đang sáng thì bỏ chọn (điện thoại không có rê chuột)
  const toggle = (key: string) => setActive((a) => (a === key ? null : key))

  return (
    <section className="min-w-0 rounded-2xl bg-raise p-5 shadow-card dark:border dark:border-rule dark:shadow-none desktop:px-6">
      <div className="flex items-center justify-between gap-3">
        <h2>Đơn hàng</h2>
        <span className="text-xs text-inkSoft">{period}</span>
      </div>
      {error ? (
        <div className="-mx-5 mt-2 desktop:-mx-6">
          <LoadError onRetry={onRetry} />
        </div>
      ) : (
        <div className={cn('mt-4 flex', phone ? 'flex-col items-center gap-5' : 'items-center gap-8')}>
          {loading ? (
            <Skeleton className={cn('shrink-0 rounded-full', phone ? 'size-[164px]' : 'size-[184px]')} />
          ) : (
            <DonutRing segments={segments} active={active} onActive={setActive} emptyLabel="Chưa có đơn" className={phone ? 'size-[164px]' : 'size-[184px]'}>
              <div className="text-[34px] font-bold leading-none tracking-tight text-ink tabular-nums">
                {countFormat.format(current ? current.value : total)}
              </div>
              <div className="mt-1.5 text-xs font-medium text-muted">{current ? current.label : total > 0 ? 'đơn' : 'Chưa có đơn'}</div>
            </DonutRing>
          )}
          <ul className={cn('grid w-full min-w-0 gap-2.5', gridCols)}>
            {tiles.map((x, i) => {
              const on = active === x.key
              return (
                <li
                  key={x.key}
                  className={i === tiles.length - 1 ? lastSpan : undefined}
                  style={{ ['--seg' as string]: `var(${x.color})` }}
                >
                  <button
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggle(x.key)}
                    onPointerEnter={(e) => e.pointerType === 'mouse' && setActive(x.key)}
                    onPointerLeave={(e) => e.pointerType === 'mouse' && setActive(null)}
                    className={cn(
                      'motion-state flex w-full min-w-0 flex-col items-start gap-1 rounded-xl bg-[rgb(var(--seg)/0.08)] px-3.5 py-3 text-left ring-1 ring-inset desktop:gap-1.5 desktop:px-4 desktop:py-4',
                      on ? 'ring-[rgb(var(--seg)/0.55)]' : 'ring-[rgb(var(--seg)/0.14)]',
                    )}
                  >
                    <span className="flex min-w-0 items-center gap-2 text-[13px] font-medium text-inkSoft">
                      <i className="size-2.5 shrink-0 rounded-full bg-[rgb(var(--seg))]" aria-hidden />
                      <span className="truncate">{x.label}</span>
                    </span>
                    <b className="text-2xl font-bold leading-tight tabular-nums text-ink desktop:text-[28px]">{loading ? '—' : countFormat.format(x.value)}</b>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </section>
  )
}

/** Độ đậm từng đoạn theo thứ tự: đoạn cuối đậm nhất. */
const PART_CLASS: Record<number, string[]> = {
  1: ['bg-toneFg'],
  2: ['bg-toneFg/30', 'bg-toneFg'],
  3: ['bg-toneFg/30', 'bg-toneFg/60', 'bg-toneFg'],
}

export function MoneyCard({
  tone,
  icon: Icon,
  title,
  value,
  parts,
  format,
  foot,
  link,
  strongFirst,
  loading,
  error,
}: {
  tone: Tone
  icon: LucideIcon
  title: string
  value: ReactNode
  parts: { label: string; value: number; to?: string }[]
  format: (n: number) => string
  foot?: ReactNode
  link?: { to: string; label: string }
  /** Đoạn đầu đậm nhất (mặc định đoạn cuối — tiền càng gần tay càng đậm) */
  strongFirst?: boolean
  loading?: boolean
  error?: boolean
}) {
  const total = parts.reduce((s, p) => s + Math.max(0, p.value), 0)
  const base = PART_CLASS[parts.length] ?? parts.map(() => 'bg-toneFg')
  const cls = strongFirst ? [...base].reverse() : base
  return (
    <section className={cn(toneClass(tone), 'flex min-w-0 flex-col gap-3.5 rounded-2xl bg-raise p-5 shadow-card dark:border dark:border-rule dark:shadow-none')}>
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2.5">
          <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-toneBg text-toneFg">
            <Icon className="size-4" aria-hidden />
          </span>
          <h2 className="truncate">{title}</h2>
        </span>
        {link && (
          <Link
            to={link.to}
            className="motion-state -my-2.5 inline-flex min-h-11 shrink-0 items-center gap-0.5 text-[13px] font-semibold text-accent hover:underline desktop:my-0 desktop:min-h-8"
          >
            {link.label}
            <ChevronRight className="size-3.5" aria-hidden />
          </Link>
        )}
      </div>
      <div className="truncate text-[28px] font-bold leading-tight tracking-tight text-ink mobile:text-[26px]">
        {loading ? <Skeleton className="h-8 w-40" /> : error ? '—' : value}
      </div>
      {error ? (
        <p className="text-xs text-muted">Không tải được số liệu</p>
      ) : (
        <>
          <div
            className="flex h-2.5 gap-[3px] overflow-hidden rounded-full bg-paperSoft"
            role="img"
            aria-label={parts.map((p) => `${p.label} ${format(p.value)}`).join(', ')}
          >
            {!loading && total > 0 &&
              parts.map((p, i) =>
                p.value > 0 ? (
                  <span key={p.label} className={cn('h-full min-w-[4px] rounded-full', cls[i])} style={{ flexGrow: p.value, flexBasis: 0 }} />
                ) : null,
              )}
          </div>
          <ul className="flex flex-col gap-1.5 text-[13px]">
            {parts.map((p, i) => {
              const row = (
                <>
                  <span className="flex min-w-0 items-center gap-2 text-inkSoft">
                    <i className={cn('size-2.5 shrink-0 rounded-[3px]', cls[i])} aria-hidden />
                    <span className="truncate">{p.label}</span>
                  </span>
                  <b className="shrink-0 font-semibold tabular-nums text-ink">{loading ? '—' : format(p.value)}</b>
                </>
              )
              return (
                <li key={p.label}>
                  {p.to ? (
                    <Link to={p.to} className="motion-state flex min-h-11 items-center justify-between gap-2 rounded-md hover:bg-paperSoft/60 desktop:-my-1 desktop:min-h-8">
                      {row}
                    </Link>
                  ) : (
                    <span className="flex items-center justify-between gap-2">{row}</span>
                  )}
                </li>
              )
            })}
          </ul>
          {foot && <p className="border-t border-rule pt-2.5 text-xs text-muted">{foot}</p>}
        </>
      )}
    </section>
  )
}

/**
 * Xếp khối Tổng quan. Máy tính: ô tiền bên trái, bên phải đơn vị trực thuộc (nếu có) trên vòng đơn hàng.
 * Điện thoại: một cột — tiền, đơn vị, đơn hàng.
 */
export function OverviewGrid({ revenue, orders, units }: { revenue: ReactNode; orders: ReactNode; units?: ReactNode }) {
  const phone = useViewportMode() === 'mobile'
  if (phone) {
    return (
      <div className="space-y-3">
        {revenue}
        {units}
        {orders}
      </div>
    )
  }
  return (
    <div className="flex flex-wrap gap-4">
      <div className="flex min-w-0 flex-[7_1_420px]">{revenue}</div>
      {/* Vòng tròn + chú thích cần ~360px; hẹp hơn thì xuống hàng riêng thay vì cắt chữ chú thích */}
      <div className="flex min-w-0 flex-[5_1_360px] flex-col gap-4">
        {units}
        {orders}
      </div>
    </div>
  )
}
